import React, { memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleDown,
  faArrowCircleUp,
  faCheckSquare,
  faWindowClose,
  faTrash,
  faEye,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import Trigger from "../../../../components/OverlayTrigger";
import {
  SortableHeader,
  StyledArrowIcon,
  TextLink,
  TruncatedText,
  ActionButtonContainer,
  EditButton,
  ViewButton,
  ActivateButton,
  DeactivateButton,
  DeleteButton,
  NoDataMessage,
  ResponsiveTableContainer,
  LoadingOverlay,
  StatusBadge,
} from "../../../../components/StyledComponents";
import { Table } from "@themesberg/react-bootstrap";
import { AdminsRoutes } from "../../../../routes";

const StaffTable = memo(
  ({
    loading,
    data,
    pageData,
    visibleColumns,
    navigate,
    selected,
    sort,
    setSort,
    setOrderBy,
    getRole,
    handleShow,
    handleDeleteModal,
    isHidden,
    isValidOrderBy,
  }) => {
    const handleSortClick = (headerValue) => {
      if (isValidOrderBy(headerValue)) {
        setOrderBy(headerValue);
      }
    };

    const handleSortDirectionClick = (e, direction) => {
      e.stopPropagation();
      setSort(direction);
    };

    return (
      <ResponsiveTableContainer>
        <LoadingOverlay className={loading ? "loading" : ""}>
          <Table striped responsive hover size="sm" className="text-center mt-3">
            <thead className="thead-light">
              <tr>
                {visibleColumns.map((h, idx) => (
                  <SortableHeader
                    key={idx}
                    sortable={isValidOrderBy(h.value)}
                    onClick={() => handleSortClick(h.value)}
                    className={selected(h) ? "selected" : ""}
                  >
                    {h.label}{" "}
                    {selected(h) &&
                      (sort === "asc" ? (
                        <StyledArrowIcon
                          icon={faArrowCircleUp}
                          onClick={(e) => handleSortDirectionClick(e, "desc")}
                        />
                      ) : (
                        <StyledArrowIcon
                          icon={faArrowCircleDown}
                          onClick={(e) => handleSortDirectionClick(e, "asc")}
                        />
                      ))}
                  </SortableHeader>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading &&
                pageData?.map(
                  ({
                    adminUserId,
                    email,
                    firstName,
                    lastName,
                    adminRoleId: roleId,
                    isActive,
                    group,
                  }) => (
                    <tr key={`${email}-${adminUserId}`}>
                      {visibleColumns.map((col) => {
                        switch (col.accessorKey) {
                          case "adminUserId":
                            return <td key="id">{adminUserId}</td>;
                          case "email":
                            return (
                              <td key="email">
                                <TextLink
                                  onClick={() =>
                                    navigate(
                                      `${AdminsRoutes.AdminDetails.split(":").shift()}${adminUserId}`
                                    )
                                  }
                                >
                                  {email}
                                </TextLink>
                              </td>
                            );
                          case "firstName":
                            return (
                              <td key="name">
                                <Trigger
                                  message={`${firstName} ${lastName}`}
                                  id={`name-${adminUserId}`}
                                >
                                  <TruncatedText
                                    id={`name-${adminUserId}`}
                                    className="text-link"
                                  >
                                    {firstName} {lastName}
                                  </TruncatedText>
                                </Trigger>
                              </td>
                            );
                          case "roleId":
                            return <td key="role">{getRole(roleId)}</td>;
                          case "group":
                            return <td key="group">{group || "-"}</td>;
                          case "isActive":
                            return (
                              <td key="status">
                                <StatusBadge className={isActive ? "success" : "danger"}>
                                  {isActive ? "Active" : "Inactive"}
                                </StatusBadge>
                              </td>
                            );
                          case "action":
                            return (
                              <td key="action">
                                <ActionButtons
                                  adminUserId={adminUserId}
                                  roleId={roleId}
                                  isActive={isActive}
                                  firstName={firstName}
                                  lastName={lastName}
                                  email={email}
                                  getRole={getRole}
                                  navigate={navigate}
                                  handleShow={handleShow}
                                  handleDeleteModal={handleDeleteModal}
                                  isHidden={isHidden}
                                />
                              </td>
                            );
                          default:
                            return null;
                        }
                      })}
                    </tr>
                  )
                )}

              {(!pageData || pageData.length === 0) && !loading && (
                <tr>
                  <NoDataMessage colSpan={visibleColumns.length}>
                    No data found
                  </NoDataMessage>
                </tr>
              )}
            </tbody>
          </Table>
        </LoadingOverlay>
      </ResponsiveTableContainer>
    );
  }
);

const ActionButtons = memo(
  ({
    adminUserId,
    roleId,
    isActive,
    firstName,
    lastName,
    email,
    getRole,
    navigate,
    handleShow,
    handleDeleteModal,
    isHidden,
  }) => {
    const staffMember = {
      adminUserId,
      firstName,
      lastName,
      email,
      isActive,
      roleId,
    };

    return (
      <ActionButtonContainer>
        <Trigger message="Edit" id={`${adminUserId}-edit`}>
          <EditButton
            onClick={() =>
              navigate(`${AdminsRoutes.EditStaff.split(":").shift()}${adminUserId}`)
            }
            hidden={isHidden({ module: { key: "Admins", value: "U" } })}
          >
            <FontAwesomeIcon icon={faEdit} />
          </EditButton>
        </Trigger>

        <Trigger message="View Details" id={`${adminUserId}-view`}>
          <ViewButton
            onClick={() =>
              navigate(`${AdminsRoutes.AdminDetails.split(":").shift()}${adminUserId}`)
            }
            hidden={isHidden({ module: { key: "Admins", value: "R" } })}
          >
            <FontAwesomeIcon icon={faEye} />
          </ViewButton>
        </Trigger>

        {!isActive ? (
          <Trigger message="Set Status Active" id={`${adminUserId}-active`}>
            <ActivateButton
              onClick={() => handleShow(staffMember)}
              hidden={isHidden({ module: { key: "Admins", value: "T" } })}
            >
              <FontAwesomeIcon icon={faCheckSquare} />
            </ActivateButton>
          </Trigger>
        ) : (
          <Trigger message="Set Status In-Active" id={`${adminUserId}-inactive`}>
            <DeactivateButton
              onClick={() => handleShow(staffMember)}
              hidden={isHidden({ module: { key: "Admins", value: "T" } })}
            >
              <FontAwesomeIcon icon={faWindowClose} />
            </DeactivateButton>
          </Trigger>
        )}

        <Trigger message="Delete" id={`${adminUserId}-delete`}>
          <DeleteButton
            onClick={() => handleDeleteModal(staffMember)}
            hidden={isHidden({ module: { key: "Admins", value: "D" } })}
          >
            <FontAwesomeIcon icon={faTrash} />
          </DeleteButton>
        </Trigger>
      </ActionButtonContainer>
    );
  }
);

export default StaffTable;
